import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { RecaptchaVerifier, signInWithPhoneNumber, linkWithPhoneNumber, reauthenticateWithPhoneNumber } from "firebase/auth";
import { Loader2, ShieldCheck } from "lucide-react";
import { auth, authError } from "@/lib/firebase";
import { API } from "@/lib/api";

export const DIAL = { IN: ["India", "+91", /^[6-9]\d{9}$/], US: ["United States", "+1", /^\d{10}$/], CA: ["Canada", "+1", /^\d{10}$/],
  GB: ["United Kingdom", "+44", /^\d{10}$/], AU: ["Australia", "+61", /^\d{9}$/], DE: ["Germany", "+49", /^\d{6,13}$/],
  FR: ["France", "+33", /^\d{9}$/], IT: ["Italy", "+39", /^\d{6,11}$/], ES: ["Spain", "+34", /^\d{9}$/], NL: ["Netherlands", "+31", /^\d{9}$/] };
const COOLDOWN = 30;
const MAX_RESENDS = 3;
const MAX_ATTEMPTS = 5;

function useCountdown() {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, () => setLeft(COOLDOWN)];
}

export function PhoneAuth({ link = false, reauth = false, onDone, onError }) {
  const [countries, setCountries] = useState(["IN"]);
  const [cc, setCc] = useState("IN");
  const [num, setNum] = useState(reauth && auth.currentUser?.phoneNumber?.startsWith("+91") ? auth.currentUser.phoneNumber.slice(3) : "");
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resends, setResends] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [left, startCooldown] = useCountdown();
  const verifier = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/auth/config`).then((r) => { const list = r.data.sms_countries.filter((c) => DIAL[c]); if (list.length) { setCountries(list); setCc(list[0]); } }).catch(() => {});
    return () => { verifier.current?.clear(); verifier.current = null; };
  }, []);

  const e164 = `${DIAL[cc][1]}${num.replace(/\D/g, "").replace(/^0+/, "")}`;
  const valid = DIAL[cc][2].test(num.replace(/\D/g, "").replace(/^0+/, ""));
  const fail = (e) => {
    const phoneTaken = (link || reauth) && ["auth/credential-already-in-use", "auth/account-exists-with-different-credential"].includes(e?.code);
    const m = phoneTaken ? "This mobile number is already connected to a different Warranty Wallet account." : authError(e);
    setErr(m); onError?.(m);
  };

  const send = async () => {
    setErr(""); setBusy(true);
    try {
      verifier.current?.clear();
      const el = document.createElement("div");
      boxRef.current.replaceChildren(el);
      verifier.current = new RecaptchaVerifier(auth, el, { size: "invisible" });
      const res = reauth ? await reauthenticateWithPhoneNumber(auth.currentUser, e164, verifier.current)
        : link ? await linkWithPhoneNumber(auth.currentUser, e164, verifier.current) : await signInWithPhoneNumber(auth, e164, verifier.current);
      setConfirm(res); setCode(""); setAttempts(0); startCooldown();
    } catch (e) {
      verifier.current?.clear(); verifier.current = null; fail(e);
    } finally { setBusy(false); }
  };
  const resend = () => { if (resends >= MAX_RESENDS) return setErr("Too many codes requested. Please try again later or use Email or Google."); setResends(resends + 1); send(); };
  const verify = async (e) => {
    e.preventDefault();
    if (attempts >= MAX_ATTEMPTS) return setErr("Too many incorrect codes. Please request a new code.");
    setErr(""); setBusy(true);
    try { const cred = await confirm.confirm(code); onDone?.(cred); }
    catch (ex) { setAttempts(attempts + 1); fail(ex); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="phone-auth">
      {!confirm ? (
        <form onSubmit={(e) => { e.preventDefault(); if (valid) send(); }} className="space-y-3">
          <label className="text-sm font-semibold text-ww-navy" htmlFor="phone-number-input">Mobile Number</label>
          <div className="flex gap-2">
            <select value={cc} onChange={(e) => setCc(e.target.value)} className="ww-input w-28 shrink-0 px-3" aria-label="Country code" data-testid="phone-country-select">
              {countries.map((c) => <option key={c} value={c}>{c} {DIAL[c][1]}</option>)}
            </select>
            <input id="phone-number-input" inputMode="numeric" autoComplete="tel-national" value={num} onChange={(e) => setNum(e.target.value.replace(/[^\d\s]/g, "").slice(0, 15))} placeholder="98765 43210" className="ww-input min-w-0 flex-1" data-testid="phone-number-input" />
          </div>
          {countries.length === 1 && <p className="text-xs text-ww-muted" data-testid="phone-country-note">Mobile sign-in is available for {DIAL[cc][0]} numbers. Elsewhere, please use Email or Google.</p>}
          <button className="ww-btn-primary w-full justify-center" disabled={!valid || busy} data-testid="phone-send-code-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send OTP</button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-3">
          <p className="text-sm text-ww-muted">Enter the 6-digit code sent to <span className="font-semibold text-ww-navy" data-testid="phone-sent-to">{e164}</span>. <button type="button" onClick={() => { setConfirm(null); setErr(""); }} className="font-semibold text-ww-emerald hover:underline" data-testid="phone-change-number">Change</button></p>
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className="ww-input text-center text-2xl tracking-[0.5em]" autoFocus data-testid="phone-otp-input" />
          <button className="ww-btn-primary w-full justify-center" disabled={code.length !== 6 || busy} data-testid="phone-verify-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Verify & Continue</button>
          <p className="text-center text-sm text-ww-muted">{left > 0 ? <span data-testid="phone-resend-timer">Resend code in {left}s</span>
            : <button type="button" onClick={resend} disabled={busy} className="font-semibold text-ww-emerald hover:underline" data-testid="phone-resend-button">Resend code</button>}</p>
        </form>
      )}
      {err && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="phone-auth-error">{err}</p>}
      <div ref={boxRef} />
    </div>
  );
}
